/*
  Cart drawer, brought in line with the presets offer page.

  Five changes, all additive to the rendered drawer:

    1. "3 Bags every 30 days" becomes "47 cups a month, billed every 90 days", and
       moves from the price cell to the details cell. Cups because that is the unit
       the PDP sells in and the unit the per-cup price is quoted in; bags is
       packaging. The cadence is read from the actual selling plan rather than
       assumed, so the prepaid plan says 90 days and the monthly plan says 4 weeks
       -- the hardcoded "30 days" was wrong for both.
    2. Bundle lines read "Smooth Talker, 1/4 Caf - 30oz" rather than "3 x Smooth
       Talker, 1/4 Caf". Same reason: a count of bags is packaging, an amount of
       coffee is the thing being bought.
    3. A per-coffee stepper on each bundle line. One tap is one 10oz bag of THAT
       coffee. Per line rather than per bundle because on a mixed set a single +
       would have to choose a coffee for the shopper; and one bag rather than a
       doubling because doubling is a different question, which the PDP already
       asks (Just me / Two of us). It also happens to be the best margin in the
       model: shipping, packout and processing are already paid on that box, so an
       extra bag lands near 70% contribution against a ~50% base.
    4. A "Save $X" chip. The theme already renders the struck compare-at price, so
       the saving was implied but never stated.
    5. The free-shipping bar reads as unlocked rather than as a static line.

  On (1), the move is load-bearing rather than cosmetic. This is a table, and a
  table column takes its width from its widest unwrapped content -- so a long
  cadence line sitting in the narrow price cell steals width from the title, which
  collapsed "THE HEIST" to three characters a line. Product information belongs in
  the details cell; the price cell holds money.

  The steppers hold the group at the subscription minimum. Dropping a set below
  three bags would leave a subscription that the PDP itself would not have sold, so
  the last bags cannot be stepped away -- the row's own remove button is the way
  out, and it takes the whole set with it.

  Not touched: the Heist Society membership row already renders exactly the
  treatment we wanted -- FREE (VALUE $70), $0.00 against a struck $70.00, and the
  gift list. It needed nothing.

  Why a separate asset rather than editing the drawer: snippets/cart-products.liquid
  is 50KB and cart-summary.liquid is 23KB, there is no partial-write API for theme
  files, and a bad character in either takes the cart down site-wide. This reads the
  rendered DOM and the cart JSON and augments what is there, which is also why it
  survives a vite redeploy.

  Re-running: the drawer is replaced wholesale on every cart mutation, so there is
  no element to hold state on -- the same morph behaviour that broke two earlier
  attempts on the PDP gallery. Every pass re-queries, marks what it has touched with
  a data attribute so it is idempotent, and re-runs after any cart write. The swap
  targets cart-items-component, which is inside the drawer's <dialog>, so the drawer
  stays open across a change.
*/
(function () {
  // Matches the PDP block's grams_per_bag / grams_per_cup settings, the 10 oz bag
  // on the label, and the block's min_subscription_bags. If those change in the
  // theme editor, change these with them.
  var CUPS_PER_BAG = 283 / 18;
  var OZ_PER_BAG = 10;
  /* Smallest subscription tier is 2 bags. Unused since the per-coffee stepper was removed,
     kept correct so it does not mislead anyone who reintroduces a quantity control. */
  var MIN_BAGS = 2;

  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function parseMoney(t) {
    var n = Number(String(t || '').replace(/[^0-9.]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  }

  function sectionId() {
    var c = document.querySelector('cart-items-component[data-section-id]');
    return c ? c.dataset.sectionId : null;
  }

  function swapDrawer(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var targets = ['cart-items-component', '.cart-drawer__inner'];
    for (var i = 0; i < targets.length; i++) {
      var next = doc.querySelector(targets[i]);
      var cur = document.querySelector(targets[i]);
      if (next && cur) { cur.replaceWith(next); return true; }
    }
    return false;
  }

  async function setLineQuantity(key, quantity) {
    var id = sectionId();
    var body = { id: key, quantity: quantity };
    if (id) {
      body.sections = id;
      body.sections_url = window.location.pathname;
    }
    var res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
    if (res && res.sections && id && res.sections[id]) swapDrawer(res.sections[id]);
    enhance();
  }

  var running = false;

  async function enhance() {
    if (running) return;
    if (!document.querySelector('.cart-items__wrapper')) return;
    running = true;
    try {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); });

      var byKey = {};
      var groups = {};
      var hasSubscription = false;
      cart.items.forEach(function (i) {
        byKey[i.key] = i;
        if (i.selling_plan_allocation) hasSubscription = true;
        var g = (i.properties || {})._bundle_group;
        if (g) { (groups[g] = groups[g] || []).push(i); }
      });

      // ---- free shipping affirmation ----
      var promo = document.querySelector('.cart-promo-bar');
      if (promo && hasSubscription && promo.dataset.heistCart !== '1') {
        promo.dataset.heistCart = '1';
        promo.classList.add('heist-ship');
        var label = document.createElement('span');
        label.className = 'heist-ship__row';
        label.innerHTML = '<span class="heist-ship__tick" aria-hidden="true"></span><span></span>';
        label.lastChild.textContent = 'Free shipping unlocked';
        var track = document.createElement('span');
        track.className = 'heist-ship__track';
        track.innerHTML = '<span class="heist-ship__fill"></span>';
        promo.textContent = '';
        promo.appendChild(label);
        promo.appendChild(track);
      }

      // ---- bundle cards ----
      document
        .querySelectorAll('tr.cart-items__table-row--card.cart-items__table-row--bundle')
        .forEach(function (row) {
          if (row.dataset.heistCart === '1') return;
          var item = byKey[row.getAttribute('data-key')];
          if (!item) return;
          var g = (item.properties || {})._bundle_group;
          var lines = groups[g] || [];
          if (!lines.length) return;
          row.dataset.heistCart = '1';

          var bags = lines.reduce(function (a, l) { return a + l.quantity; }, 0);
          var plan = (lines[0].selling_plan_allocation || {}).selling_plan;
          var prepaid = !!(plan && /prepaid/i.test(plan.name));

          // Cups per month. bags is one shipment's worth, which is what the PDP
          // quotes, so the two pages agree by construction.
          var cups = Math.round(bags * CUPS_PER_BAG);

          // "3 x Smooth Talker, 1/4 Caf" -> "Smooth Talker, 1/4 Caf - 30oz", plus a
          // stepper for that coffee. The count is read off the line's own text, so
          // this does not depend on the DOM and the cart JSON being in the same
          // order, and it is self-limiting: once rewritten there is no leading
          // "N x" left for the pattern to match on a later pass.
          row.querySelectorAll('.cart-items__bundle li').forEach(function (li) {
            var m = /^\s*(\d+)\s*x\s*(.+?)\s*$/.exec(li.textContent);
            if (!m) return;
            var qty = Number(m[1]);
            var name = m[2];

            var line = null;
            for (var i = 0; i < lines.length; i++) {
              var l = lines[i];
              var full =
                l.product_title +
                (l.variant_title && l.variant_title !== 'Default Title' ? ', ' + l.variant_title : '');
              if (full === name && l.quantity === qty) { line = l; break; }
            }

            li.textContent = '';
            var text = document.createElement('span');
            text.textContent = name + ' - ' + qty * OZ_PER_BAG + 'oz';
            li.appendChild(text);

            /* NO PER-COFFEE STEPPER. Removed 2026-08-28.

               A subscription is bound to a selling plan carrying a PER-BAG price for a SPECIFIC
               bag count - "Ships every 4 weeks (3 bags)" is $23.00 a bag. Nudging quantity here
               changes the bags but NOT the plan, so the shopper silently lands on the wrong
               price: 2 bags left on the 3-bag plan bills $52.66 instead of $59.00, and 3 bags on
               the 2-bag plan bills $88.50 instead of $79.00. Seen in a real checkout.

               The trash control removes the whole set, which is unambiguous and correctly
               priced. Do not restore this until a quantity change also moves the subscription
               onto the plan matching the new bag count. */
          });

          var info =
            row.querySelector('.cart-items__details .cart-items__product-info') ||
            row.querySelector('.cart-items__details');

          var ships = row.querySelector('.cart-items__bundle-ships');
          if (ships) {
            ships.textContent =
              cups + ' cups a month · billed ' + (prepaid ? 'every 90 days' : 'every 4 weeks');
            // Out of the price cell. See the header note on table column widths.
            if (info) info.appendChild(ships);
          }

          var priceRow = row.querySelector('.cart-items__bundle-price-row');
          var compareEl = row.querySelector('.cart-items__price-compare');
          if (priceRow && compareEl) {
            var paid = lines.reduce(function (a, l) { return a + l.line_price; }, 0);
            var was = parseMoney(compareEl.textContent);
            if (was > paid) {
              var chip = document.createElement('span');
              chip.className = 'heist-cart__save t-n2-med';
              chip.textContent = 'Save ' + money(was - paid);
              priceRow.insertAdjacentElement('afterend', chip);
            }
          }
        });
    } catch (e) {
      /* a cart that will not parse is not worth breaking the drawer over */
    } finally {
      running = false;
    }
  }

  // Re-run after any cart write. Deliberately does NOT match /cart.js, which this
  // script fetches itself -- that would schedule an endless re-enhance.
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var p = nativeFetch.apply(this, arguments);
    try {
      var a = arguments[0];
      var url = String((a && a.url) || a || '');
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () { setTimeout(enhance, 150); });
      }
    } catch (e) {}
    return p;
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[href*="/cart"], .header__cart, [data-cart-toggle]')) {
      setTimeout(enhance, 250);
    }
  }, true);

  function boot() {
    enhance();
    var n = 0;
    var t = setInterval(function () { n++; enhance(); if (n > 20) clearInterval(t); }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/*
  CART COUNT RECONCILER, SITE-WIDE  (2026-10-10)

  Every add from an EMPTY cart showed "CART (0)" in the header and drawer title with paid
  bags sitting in the drawer - Autoship START NOW and Buy it once alike, on both PDPs.

  The reconciler that fixes this already exists (syncCartCounts in
  snippets/heist-subscription-minimum.liquid) but that snippet renders inside the cart
  summary, which is not on the page when the cart is empty. A shopper who lands with an
  empty cart - nearly everyone - never had it. Swapped-in markup does not run scripts, so
  it never boots later either.

  This runs from a file that loads on every page. Same counting rule as that snippet:
  $0 lines (the gifts) do not count, measured on original_line_price. It only edits the
  digits in text nodes and only when they differ, so it cannot loop with the observer.
*/
(function () {
  if (window.__heistCountSync) return;
  window.__heistCountSync = true;
  var timer = null;

  function sync() {
    fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = (cart.items || []).reduce(function (sum, i) {
          return (i.original_line_price || 0) === 0 ? sum : sum + (i.quantity || 0);
        }, 0);
        document.querySelectorAll('.heist-cart, #cart-drawer .theme-drawer__title').forEach(function (el) {
          var w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null), node;
          while ((node = w.nextNode())) {
            var t = node.nodeValue;
            if (!/\d/.test(t)) continue;
            var next = t.replace(/\d+/g, String(n));
            if (next !== t) node.nodeValue = next;
          }
        });
      })
      .catch(function () {});
  }

  /* Two settle points, because the buy box replaces lines in a burst and a section swap
     requested mid-burst can repaint a stale number after the first pass. */
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { sync(); setTimeout(sync, 900); }, 250);
  }

  function watch() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
    document.addEventListener('cart-updated', schedule);
    document.addEventListener('cart:update', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
